import * as moment from 'moment';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { Box, Typography, Link } from '@mui/material';
import { BillingIntervalEnum } from 'enums';
import { useTeamContext } from 'contexts/useTeamContext';
import useStripeSubscription from 'hooks/useStripeSubscription';
import PlanField from 'components/PlanField';
import PlanPaymentMethod from 'components/PlanPaymentMethod';
import Button from 'components/Button';
import CustomButton from 'components/CustomButton';
import AlertAlt from 'components/AlertAlt';
import {
  Card,
  TitleWrapper,
  Title,
  Separator,
  PlanFieldContainer,
  AmountContainer,
  ChangeButtonContainer,
  BillingInterval,
} from 'styles/components/planDetailsCard';
import { flowTrialActive, getOneCheckoutHostUrl, openMtgChat } from 'utils';
import OwnerPlanDetailsCardAlt from 'components/OwnerPlanDetailsCardAlt';
import { checkUserCountry } from 'utils/locale';
import LoadingCircle from 'components/LoadingCircle';
import usePlan from 'hooks/usePlan';
import CancelSubscriptionModalAlternative from 'components/CancelSubscriptionModalAlternative';

const Props = {
  user: PropTypes.object.isRequired,
  changePlanAction: PropTypes.bool,
  paymentMethodUpdate: PropTypes.bool,
};

export default function OwnerPlanDetailsCard({ user, changePlanAction }) {
  const [team] = useTeamContext();
  const router = useRouter();
  const currentStripeSubscription = useStripeSubscription();
  const [latestInvoice, setLatestInvoice] = useState(null);
  const [userIsChina, setUserIsChina] = useState(false);
  const [userIsJapan, setUserIsJapan] = useState(false);
  const [plan, , isLoadingPlan] = usePlan();
  const [showModal, setShowModal] = useState(false);
  const isInvoiceOpen = latestInvoice?.status === 'open' && latestInvoice?.attempt_count === 0;
  const isInvoiceError =
    latestInvoice?.status === 'unpaid' ||
    (latestInvoice?.attempt_count > 0 && latestInvoice?.status !== 'paid');

  async function getCountryFromUser() {
    const isChina = await checkUserCountry.isChina(user);
    const isJapan = await checkUserCountry.isJapan(user);

    setUserIsChina(isChina);
    setUserIsJapan(isJapan);
  }

  // function handleOpenIntercomChat() {
  //   if (typeof window.Intercom === 'function') {
  //     window.Intercom('show');
  //   }
  // }
  function handleOpenIntercomChat() {
    openMtgChat();
  }

  function getRedirectToBillingUrl(endpoint = '/payment-billing-history') {
    return getOneCheckoutHostUrl(user.country_code) + endpoint;
  }

  useEffect(() => {
    setLatestInvoice(currentStripeSubscription?.latest_invoice);
  }, [currentStripeSubscription]);

  const planTitles = {
    R: 'Researcher Subscription',
    L: 'Teams & Labs Subscription',
    S: 'Student Subscription',
    T: 'Starter Subscription',
  };

  useEffect(() => {
    getCountryFromUser();
  }, [user]);

  function getPaymentMethodTitle() {
    if (plan.paymentMethod instanceof Object) {
      return `${plan.paymentMethod.brand.toUpperCase()} ending in ${plan.paymentMethod.lastDigits}`;
    }

    return plan.paymentMethod;
  }

  if (isLoadingPlan) {
    return <LoadingCircle heightProps="456px" />;
  }

  if (flowTrialActive && !(userIsChina || userIsJapan)) {
    return <OwnerPlanDetailsCardAlt user={user} changePlanAction={changePlanAction} plan={plan} />;
  }

  if (plan) {
    return (
      <Card>
        <TitleWrapper>
          <Box>
            <Title>{planTitles[plan.plan]}</Title>

            {plan.billingInterval === BillingIntervalEnum.YEAR && (
              <BillingInterval>Paid Yearly</BillingInterval>
            )}
            {plan.billingInterval === BillingIntervalEnum.MONTH && (
              <BillingInterval>Paid Monthly</BillingInterval>
            )}
            {plan.billingInterval === BillingIntervalEnum.SEMESTER && (
              <BillingInterval>Paid Semiannually</BillingInterval>
            )}
            {plan.billingInterval === BillingIntervalEnum.QUARTER && (
              <BillingInterval>Paid Quarterly</BillingInterval>
            )}
          </Box>

          {isInvoiceError && (
            <Box maxWidth={320}>
              <AlertAlt severity="error">
                Alert:{' '}
                <Link href="/my-account/billing/update-card" passHref>
                  Credit card update
                </Link>{' '}
                required.
              </AlertAlt>
            </Box>
          )}

          {isInvoiceOpen && (
            <Box maxWidth={320}>
              <AlertAlt severity="warning">
                You have an open invoice.{' '}
                <Link href="/my-account/billing/update-card" passHref>
                  Click here
                </Link>{' '}
                to confirm the subscription.
              </AlertAlt>
            </Box>
          )}
        </TitleWrapper>

        <Separator />

        <PlanFieldContainer>
          <PlanField
            icon="account_balance_wallet"
            label="Amount"
            value={`${plan.currency} ${Math.round(plan.total - plan.discount)}`}
          />
        </PlanFieldContainer>

        {!plan.cancelAt ? (
          <AmountContainer>
            {
              <PlanPaymentMethod
                title={getPaymentMethodTitle()}
                onUpdateClick={
                  plan.paymentMethod instanceof Object
                    ? () => router.push('/my-account/billing/update-card')
                    : null
                }
              />
            }

            {plan.resumesAt ? (
              <PlanField
                icon="schedule"
                label="Resumes At"
                value={moment.unix(plan.resumesAt).format('MM/DD/YYYY')}
              />
            ) : (
              <PlanField
                icon="schedule"
                label="Next Renewal"
                value={plan.nextCharge ? moment.utc(plan.nextCharge).format('MM/DD/YYYY') : 'N/A'}
              />
            )}
          </AmountContainer>
        ) : (
          <Box mb={3}>
            <AlertAlt icon={false} severity="default">
              Your plan has been <b>canceled</b> and will be changed to <b>Starter</b> on{' '}
              {moment.utc(plan.cancelAt).format('MM/DD/YYYY')}
              {team && team.invites.length > 0 && (
                <Box mt={3} my={2}>
                  <Typography>
                    <b>And will affect the following Teams & Labs members:</b>
                  </Typography>
                  <Typography>
                    {team.invites.map((invite) => invite.name || invite.email).join(', ')}
                  </Typography>
                </Box>
              )}
            </AlertAlt>
          </Box>
        )}

        {!!changePlanAction ? (
          <ChangeButtonContainer>
            <Button onClick={() => router.push('/my-account/billing/available-plans')}>
              Change Plan
            </Button>
          </ChangeButtonContainer>
        ) : (
          <>
            <ChangeButtonContainer>
              <Button onClick={() => router.push('/my-account/billing/plan')}>Manage Plan</Button>
            </ChangeButtonContainer>
            <ChangeButtonContainer>
              <Button onClick={() => router.push('/my-account/billing/history')}>
                View Invoices
              </Button>
            </ChangeButtonContainer>

            {!plan.cancelAt && (
              <CustomButton
                onClick={() => setShowModal(true)}
                variant="ctaPinkOutline"
                style={{ maxHeight: 40 }}
              >
                Cancel subscription
              </CustomButton>
            )}
          </>
        )}

        <CancelSubscriptionModalAlternative
          plan={plan}
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      </Card>
    );
  }

  return (
    <Card>
      {'pack_title' in user && user.pack_title ? (
        <Title>{user.pack_title} Subscription</Title>
      ) : (
        <Title>{planTitles[user.tp_user]}</Title>
      )}
      {user.dt_end && (
        <Box mt={3} mb={3}>
          <PlanField
            icon="schedule"
            label="Next Renewal"
            value={moment.utc(user.dt_end).format('MM/DD/YYYY')}
          />
        </Box>
      )}

      <Box mb={3}>
        <ChangeButtonContainer>
          <Link href={getRedirectToBillingUrl()} style={{ textDecoration: 'none' }}>
            <Button>Manage Plan</Button>
          </Link>
        </ChangeButtonContainer>
        <ChangeButtonContainer>
          <Link href={getRedirectToBillingUrl()} style={{ textDecoration: 'none' }}>
            <Button>View Invoices</Button>
          </Link>
        </ChangeButtonContainer>
      </Box>

      <Box mb={3}>
        <AlertAlt icon={false} severity="warning">
          Due to an update in the payment system, some older accounts may not be managed directly in
          the website. Please{' '}
          <Link onClick={handleOpenIntercomChat}>contact the customer success team</Link> for any
          request.
        </AlertAlt>
      </Box>
    </Card>
  );
}

OwnerPlanDetailsCard.propTypes = Props;
